import { Component, Injector, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, ResolveFn, Router } from '@angular/router';
import { ConfirmationDialogComponent } from '@app/components';
import { SocketEventTypes } from '@app/enums';
import { AbilityService, AuthService, FileHelperService, NotifyService, SocketService } from '@app/services';
import { FeatureService } from '@app/services/feature.service';
import { DocumentsV2Resolver } from '@documents-v2/documents-v2.resolver';
import {
    COMPANY_DOCUMENTS_TAB_MENU,
    companyDocumentsMenuServiceFactory,
} from '@documents-v2/menus/company-documents-show-menu.service';
import { CompanyDocumentAssignmentsTable, OfferLetterTemplateAssignmentsTable } from '@documents-v2/services';
import { MultiSignerStatusComponent } from '@documents-v2/standalone-components';
import { Model } from '@models/core/base.model';
import { CompanyDocument } from '@models/documents/company-document.model';
import { DocumentAcknowledgement } from '@models/documents/document-acknowledgement.model';
import { SignatureRequest } from '@models/documents/signature-request.model';
import { Candidate } from '@models/offer-letters/candidate.model';
import { TranslateService } from '@ngx-translate/core';
import { OnboardingStatusService } from '@self-serve/services/onboarding-status.service';
import { MenuColumnEvent } from '@table/interfaces/menu-column-event.interface';
import { Subscription } from 'rxjs';

@Component({
    templateUrl: './company-document-assignments.template.html',
    styleUrls: ['./company-document-assignments.style.scss'],
    providers: [
        {
            provide: COMPANY_DOCUMENTS_TAB_MENU,
            useFactory: companyDocumentsMenuServiceFactory,
            deps: [
                AuthService,
                AbilityService,
                FeatureService,
                ActivatedRoute,
                OnboardingStatusService,
                TranslateService,
            ],
        },
    ],
})
export class CompanyDocumentAssignmentsView implements OnInit, OnDestroy {
    @ViewChild('multiSignerStatus', { static: true }) multiSignerStatus!: MultiSignerStatusComponent;
    @ViewChild('confirmDeleteDialog', { static: true }) confirmDeleteDialog!: ConfirmationDialogComponent;

    isLoading = true;
    table: CompanyDocumentAssignmentsTable | OfferLetterTemplateAssignmentsTable | undefined;
    companyDocument: CompanyDocument = new CompanyDocument();
    currentAssignment: SignatureRequest | DocumentAcknowledgement | undefined;
    isMultiSignerStatusModalOpen = false;

    private resolver: {
        resolve: ResolveFn<any>;
    };
    private tableSub: Subscription | undefined;
    private socketSub: Subscription;

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private auth: AuthService,
        private notify: NotifyService,
        private injector: Injector,
        private socket: SocketService,
        private fileHelper: FileHelperService
    ) {
        this.resolver = this.injector.get(DocumentsV2Resolver);
        this.socketSub = this.socket.getCompanyChannel(SocketEventTypes.documentDownloaded).subscribe(() => {
            this.table?.reload();
        });
    }

    async ngOnInit(): Promise<void> {
        this.reResolve();
    }

    ngOnDestroy(): void {
        this.tableSub?.unsubscribe();
        this.socketSub.unsubscribe();
    }

    onMenuItemSelected(event: MenuColumnEvent): void {
        if (event.menuItem.event === 'download' && event.data instanceof SignatureRequest) {
            this.onDownload(event.data);
            return;
        }
        this.currentAssignment = <SignatureRequest | DocumentAcknowledgement>event.data;
        if (event.menuItem.event === 'delete') {
            this.confirmDeleteDialog
                .open()
                .then((resolver: boolean) => {
                    if (resolver && this.currentAssignment) {
                        this.deleteAssignment(this.currentAssignment);
                    }
                })
                .catch((err) => this.notify.error(err));
            return;
        }

        this.companyDocument
            .notify(this.currentAssignment)
            .then(() => this.notify.success('document.reminderSent'))
            .catch((err) => this.notify.error(err));
    }

    onRemindIndividual(signatureRequest: SignatureRequest): void {
        this.companyDocument.notify(signatureRequest, false);
        this.notify.success('document.reminderSent');
    }

    showMultiSignerStatusModal(assignment: SignatureRequest | DocumentAcknowledgement | Candidate): void {
        if (assignment instanceof SignatureRequest && assignment.isMultiSigner()) {
            // Modal for Signature Requests with 2 or more co-signers
            this.multiSignerStatus.combineRequestWithCosignersForDisplay(assignment);
            this.isMultiSignerStatusModalOpen = true;
        }
        if (assignment instanceof Candidate) {
            // Redirect to Offer Letter journey
            this.router.navigate(['/employees', 'offer-letters', 'candidates', String(assignment.id)]);
        }
    }

    reResolve(): void {
        this.isLoading = true;
        this.resolver
            .resolve(this.route.snapshot, this.router.routerState.snapshot)
            .then((models: Map<string, Model>) => {
                this.companyDocument = <CompanyDocument>models.get('companyDocument');
                if (this.companyDocument.isOfferLetter()) {
                    this.table = new OfferLetterTemplateAssignmentsTable(this.auth, this.companyDocument);
                    this.table.unnavigableLink = true;
                } else {
                    this.table = new CompanyDocumentAssignmentsTable(this.auth, this.companyDocument);
                }
                this.tableSub = this.table.onLoading.subscribe((isLoading) => (this.isLoading = isLoading));
                this.isLoading = false;
            })
            .catch((err: unknown) => (err as Error).message || 'document.documentReloadError');
    }

    private async deleteAssignment(assignment: SignatureRequest | DocumentAcknowledgement): Promise<void> {
        assignment instanceof SignatureRequest
            ? assignment.param('signingTemplate', assignment.signingTemplateId)
            : assignment.param('companyDocument', assignment.companyDocumentId);

        this.isLoading = true;
        await assignment.delete();
        this.table?.reload();
        this.notify.success('document.unassigned');
        this.isLoading = false;
    }

    private onDownload(signatureRequest: SignatureRequest): void {
        this.fileHelper.save(
            signatureRequest.file.id,
            this.companyDocument.name + ' - ' + signatureRequest.employee.fullName,
            signatureRequest.file.type ?? ''
        );
    }
}
